import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'
import { capability, MANAGED_SERVICES } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.
const email = capability('email')!
const managedEmail = MANAGED_SERVICES.find((s) => s.id === 'managed-email')!

const PROBLEMS = [
  {
    problem: 'Social reach keeps shrinking',
    description:
      'Your followers see a fraction of what you post, and the algorithm decides which fraction. Email goes to everyone who asked for it, every time.',
  },
  {
    problem: 'Your list lives in three places',
    description:
      'Ticket buyers in one spreadsheet, donors in another tool, newsletter subscribers in a third. Nobody knows which names overlap.',
  },
  {
    problem: 'Campaigns take a back seat to the season',
    description:
      'Season announcements, ticket launches, and reminders all land during your busiest weeks. The email that would sell the concert never gets written.',
  },
  {
    problem: 'Generic tools do not know music',
    description:
      'Mailchimp does not know what a season, a program, or a call time is. You end up fighting the tool instead of writing to your audience.',
  },
]

const FAQS = [
  {
    q: 'What does the self-serve plan include?',
    a: `Newsletters, sequences, and automations for up to 2,000 contacts at $${email.priceMonthly} a month, with a step-up to 10,000 contacts at $35. No send metering, so a busy concert week never costs extra.`,
  },
  {
    q: 'What does Managed Email look like month to month?',
    a: `We plan, write, design, and send about 2 campaigns a month plus 1 automation for $${managedEmail.priceMonthly} a month, timed to your season: announcements, on-sale pushes, concert reminders, year-end appeals. The software is included.`,
  },
  {
    q: 'Can we start self-serve and hand it off later?',
    a: 'Yes. Everything runs on the same platform, so moving between running it yourself and having us run it is a conversation, not a migration.',
  },
  {
    q: 'What about our existing list?',
    a: 'Simple imports are part of getting set up. Large or messy list migrations are scoped separately, and exporting your list back out is free, always.',
  },
]

export default function EmailMarketingPage() {
  const breadcrumbs = generateBreadcrumbs('/services/email-marketing', breadcrumbLabels)

  useSEO({
    title: 'Email Marketing for Music Organizations',
    description:
      'Newsletters, sequences, and season campaigns for music organizations. Run it yourself from $25/mo, or have Fugue Lab plan, write, and send for you.',
    url: '/services/email-marketing',
    canonical: '/services/email-marketing',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Email Marketing for Music Organizations',
      description:
        'Email marketing software and managed campaign services for festivals, churches, choruses, and music schools.',
      url: '/services/email-marketing',
    }),
  })

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const pricingRef = useStaggeredGrid<HTMLDivElement>('.pricing-card', { stagger: 0.12 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Email marketing that fills seats</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your audience already said yes once. Email is how you bring them back: season announcements, on-sale days,
          concert reminders, and appeals that land when they should.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Run it yourself for ${email.priceMonthly} a month, or have us plan, write, and send it for you. Either way it
          runs on the same platform as your website, donations, and ticketing.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
          <Link to="/packages">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              See All Pricing
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why email, and why here</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {PROBLEMS.map((item) => (
            <motion.div
              key={item.problem}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Unified CRM differentiator */}
      <section className="border-primary/30 bg-primary/5 mx-auto max-w-4xl space-y-4 rounded-2xl border p-8 text-center">
        <h2 className="text-fluid-h2">One audience, one record</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          Your subscribers, donors, and ticket buyers are the same people. Because email runs on the same platform as
          your giving and ticketing, every name is one record: you can email everyone who bought a ticket last season
          but has not given, without exporting a single spreadsheet.
        </p>
        <p className="text-muted-foreground mx-auto max-w-2xl text-sm">
          Point tools like Mailchimp plus Donorbox mean two lists that drift apart. Here they cannot drift, because they
          are the same list. See also{' '}
          <Link to="/services/donor-management" className="text-primary underline">
            Donor Management
          </Link>
          .
        </p>
      </section>

      {/* Pricing */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">Two ways to run it</h2>
        <div ref={pricingRef} className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2">
          <motion.div
            className="pricing-card border-border rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <h3 className="mb-2 text-2xl font-bold">{email.name}</h3>
            <div className="mb-4 flex items-baseline gap-1">
              <span className="text-4xl font-bold">${email.priceMonthly}</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <ul className="mb-8 space-y-3">
              {[
                'Newsletters, sequences, automations',
                'Up to 2,000 contacts ($35/mo up to 10,000)',
                'No send metering',
                'Self-serve, available today',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/get-started" className="block">
              <Button variant="outline" className="w-full cursor-pointer">
                Get Started
              </Button>
            </Link>
          </motion.div>
          <motion.div
            className="pricing-card border-primary/50 bg-primary/5 rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-primary mb-2 text-sm font-medium">Done For You</div>
            <h3 className="mb-2 text-2xl font-bold">{managedEmail.name}</h3>
            <div className="mb-4 flex items-baseline gap-1">
              <span className="text-4xl font-bold">${managedEmail.priceMonthly}</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <ul className="mb-8 space-y-3">
              {[
                'We plan, write, design, and send',
                'About 2 campaigns per month plus 1 automation',
                'Timed to your season',
                'Email software included',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/services/done-for-you#managed-email" className="block">
              <Button className="w-full cursor-pointer">Learn More</Button>
            </Link>
          </motion.div>
        </div>
        <p className="text-muted-foreground text-center text-sm">
          Product details live on the{' '}
          <Link to="/platform/features/email" className="text-primary underline">
            Email feature page
          </Link>
          . Pay annually on any plan and get 2 months free.
        </p>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {FAQS.map((f) => (
            <div key={f.q} className="faq-item border-border rounded-lg border p-6">
              <h3 className="text-foreground mb-2 font-semibold">{f.q}</h3>
              <p className="text-muted-foreground text-sm">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="pt-4 text-center">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
        </div>
      </section>
    </div>
  )
}
